import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { projects } from '../../Data/projects';
import { projectToPillarData, TabDefinition } from '../../lib/pillarTypes';
import {
  getProjectOverride,
  setProjectOverride,
} from '../../Data/ProjectConfig/projectConfigOverrides';
import PillarPageShell from '../../Components/PillarPage/PillarPageShell';
import FollowButton from '../../Components/FollowButton/FollowButton';
import ProjectConfigModal from '../../Components/ProjectConfigModal/ProjectConfigModal';
// DEV-841 / DEV-840 Path A: see KpiWizardLauncher for rationale.
import { KpiWizardLauncher } from '../../Components/KpiWizardLauncher/KpiWizardLauncher';
import CoreTabPanel from './CoreTabPanel';
import TeamListPage from '../../Components/ProjectTeam/TeamListPage';
import JournalTab from '../../Components/ProjectJournal/JournalTab';
import CommunityTabPanel from './CommunityTabPanel';
import WorkTabPanel, { type WorkSubTabKey } from './WorkTabPanel';
import ImpactTabPanel from './ImpactTabPanel';
import MediaTabPanel from './MediaTabPanel';
import { canAdminProject, canEditProjectContent, canViewProject } from '../../security/securityAdapter';
import { useApiProjectRoles } from '../../api/useApiProjectRoles';
import { fetchProjectById, toSeedShape } from '../../api/projects';
import { fetchOwnerDisplay } from '../../api/ownerDisplay';
import { mediaSrc } from '../../api/mediaUrl';
import { useHeroEdit } from '../../Components/PillarPage/useHeroEdit';
// DEV-932: securityUsers now sourced from the API via useApiSecurityData.
import { useApiSecurityData } from '../../api/useApiSecurityData';
// DEV-965: community entities are needed to distinguish user-owned from
// community-owned projects before the owner-avatar fetch.
import { useApiCommunityData } from '../../api/useApiCommunityData';
import styles from './ProjectDetail.module.css';

/* ------------------------------------------------------------------ */
/*  Tab key ↔ index mapping                                            */
/* ------------------------------------------------------------------ */

const ALL_TAB_KEYS = ['work', 'impact', 'about', 'expert', 'community', 'media'] as const;
type TabKey = (typeof ALL_TAB_KEYS)[number];

/** Resolve a URL tab key to an index within a dynamic tabs array.
 *  Falls back to the first visible tab if key is missing or unknown. */
function tabKeyToIndex(key: string | undefined, tabKeys: string[]): number {
  if (!key) return 0; // default to first visible tab
  const idx = tabKeys.indexOf(key);
  return idx >= 0 ? idx : 0;
}

/* ------------------------------------------------------------------ */
/*  Hero image edit                                                    */
/*                                                                     */
/*  Path Y, hero unification — the inline CropOverlay component and   */
/*  all hero edit state, helpers, and JSX used to live here. Both     */
/*  Full Project and Mini-Project now consume the same shared hook +  */
/*  components from Components/PillarPage/useHeroEdit and             */
/*  HeroCropOverlay. See the useHeroEdit({...}) call inside the       */
/*  component body below.                                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Props from App context (passed via route element wrapper)          */
/* ------------------------------------------------------------------ */

export interface ProjectDetailPageProps {
  currentUserGuid: string;
  debugMode?: boolean;
  onProjectConfigChanged?: () => void;
  /** The full computed Data array from App — used to find imported projects too. */
  projectData?: any[];
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ProjectDetailPage({
  currentUserGuid,
  debugMode,
  onProjectConfigChanged,
  projectData,
}: ProjectDetailPageProps) {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locState = location.state as any;
  const returnTo = locState?.returnTo as string | undefined;
  const returnLabel = locState?.returnLabel as string | undefined;
  const restoreProjectGuid = locState?.restoreProjectGuid as string | undefined;
  const incomingWorkSubTab = locState?.workSubTab as WorkSubTabKey | undefined;
  const incomingTeamView = locState?.initialTeamView as string | undefined;

  /* ---- Lookup project ---- */
  /* First check the computed Data array (includes imported projects), fall back to seed data */
  // enrichedRow is the KPI-enriched Data row (has schedule_indicator_color, etc.)
  // rawProject is the underlying project record (has project_GUID, project_name, etc.)
  const { enrichedRow, rawProject } = useMemo(() => {
    const idUpper = String(id || "").toUpperCase();
    if (projectData?.length) {
      const dataRow = projectData.find(
        (d: any) => String(d?.project_guid ?? d?.project_GUID ?? '').toUpperCase() === idUpper
      );
      if (dataRow) {
        return {
          enrichedRow: dataRow,
          rawProject: dataRow?.project ?? dataRow,
        };
      }
    }
    // Fall back to seed projects (no KPI enrichment available)
    const seedProject = (projects as any[]).find(
      (p) => String(p.project_GUID ?? p.project_guid ?? "").toUpperCase() === idUpper
    );
    return { enrichedRow: seedProject, rawProject: seedProject };
  }, [id, projectData]);

  /* ---- Direct API fallback for newly-created projects ---- */
  // When the project isn't in the cache yet (race condition after creation),
  // fetch it directly from the API.
  const [apiFallback, setApiFallback] = useState<{ enrichedRow: any; rawProject: any } | null>(null);
  const apiFetchAttempted = useRef(false);

  useEffect(() => {
    // Only attempt fallback if cache lookup found nothing AND we haven't tried yet
    if (enrichedRow || !id || apiFetchAttempted.current) return;
    apiFetchAttempted.current = true;

    fetchProjectById(id)
      .then((resp) => {
        const seed = toSeedShape(resp);
        setApiFallback({ enrichedRow: seed, rawProject: seed });
      })
      .catch(() => {
        // Genuinely not found — leave apiFallback null so "Not Found" renders
      });
  }, [id, enrichedRow]);

  // Reset fallback attempt when navigating to a different project
  useEffect(() => {
    apiFetchAttempted.current = false;
    setApiFallback(null);
  }, [id]);

  // Resolve final project data: cache first, then API fallback
  const resolvedEnrichedRow = enrichedRow || apiFallback?.enrichedRow;
  const resolvedRawProject = rawProject || apiFallback?.rawProject;

  const basePillarData = useMemo(
    () => (resolvedRawProject ? projectToPillarData(resolvedRawProject) : null),
    [resolvedRawProject]
  );

  /* ---- Resolve owner avatar ---- */
  // DEV-895 Phase 2 / DEV-983 — Was: a sysadmin-of-the-VIEWER short-circuit
  // that set the L4E logo and skipped the fetch. That was wrong on two
  // counts: (1) the sysadmin check was supposed to test whether the
  // OWNER is a sysadmin (so the project displays the L4E platform brand),
  // but the fallback to currentUserGuid meant it tested the VIEWER's
  // sysadmin status; and (2) it bypassed the entire ownership cascade,
  // so community-owned projects never showed their community's logo when
  // viewed by an admin. Removed — fetchOwnerDisplay now handles every
  // case uniformly (community cascade, workshop → user, direct user).
  const { securityUsers: _securityUsers } = useApiSecurityData();

  // DEV-1050 cutover complete — viewer-side sysadmin check was only used by
  // useShadowPermissions below, which has been removed. Hook call retained
  // for its side effect of priming the securityUsers cache used elsewhere.

  const [ownerAvatarUrl, setOwnerAvatarUrl] = useState<string | null>(null);
  // DEV-991: also remember the resolved owner's kind / guid / displayName so
  // the hero avatar can navigate to the owner's page on click.
  const [ownerInfo, setOwnerInfo] = useState<{ kind: 'user' | 'community' | 'unknown'; guid: string; displayName: string } | null>(null);
  // DEV-895 Phase 2 / DEV-982 — Was `ownerAvatarFetched = useRef(false)`,
  // a boolean guard set on first run. That ran too early — when
  // ownerCheckCommunityEntities was still the empty async-hook array.
  // The helper resolved to nothing, the boolean stuck true, and the
  // effect never re-fetched once entities actually loaded. Same class of
  // bug as the DEV-932 regressions documented in CLAUDE.md. Replaced
  // with a string key that includes whether entities are populated, so
  // the effect retries exactly once when async data arrives.
  const ownerResolvedForKey = useRef<string>('');

  // DEV-895 Phase 2 / DEV-965 — single fetchOwnerDisplay resolves the
  // community-vs-user split, walks the logo-inheritance cascade for
  // community owners, and falls back to the user's profileImageUrl for
  // user-owned projects (including workshop pattern per ADR-006).
  const {
    communityEntities: ownerCheckCommunityEntities,
    communityRelationships: ownerCheckRelationships,
  } = useApiCommunityData();

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    // DEV-895 Phase 2 / DEV-982 — Resolve target owner GUID.
    // Order of precedence:
    //   1. localStorage override (admin manually set an owner for testing
    //      or to record a recent transfer)
    //   2. canonical project.community_id from the seed-shape row
    //   3. owning_community_entity_guid alias (legacy reader compatibility)
    //   4. primary_owner_org_guid (server-derived org root, legacy rows)
    // We deliberately do NOT fall back to currentUserGuid — that gave the
    // logged-in user's avatar for every project they viewed, regardless
    // of ownership.
    let targetGuid: string | undefined;
    try {
      const ov = getProjectOverride(id) as any;
      targetGuid = ov?.owner_guid;
    } catch { /* no override */ }
    if (!targetGuid) {
      const rp = rawProject as any;
      targetGuid = rp?.community_id || rp?.owning_community_entity_guid || rp?.primary_owner_org_guid;
    }
    if (!targetGuid) return;

    // Retry-once key: refetch when entities transition from empty
    // (async hook still loading) to populated. After that, same key →
    // no extra fetch.
    const entitiesLoaded = (ownerCheckCommunityEntities ?? []).length > 0;
    const key = `${targetGuid}|${entitiesLoaded ? 'loaded' : 'empty'}`;
    if (ownerResolvedForKey.current === key) return;
    ownerResolvedForKey.current = key;

    const tg = targetGuid;
    fetchOwnerDisplay(tg, ownerCheckCommunityEntities, ownerCheckRelationships)
      .then((display) => {
        if (cancelled) return;
        if (display.avatarUrl) setOwnerAvatarUrl(display.avatarUrl);
        // DEV-991 — record the kind/guid for the avatar's click target.
        setOwnerInfo({ kind: display.kind, guid: tg, displayName: display.displayName });
      })
      .catch(() => { /* fallback to initial letter */ });

    return () => { cancelled = true; };
  }, [id, rawProject, ownerCheckCommunityEntities, ownerCheckRelationships]);

  /* Merge owner avatar into pillar data (resolved asynchronously) */
  const pillarData = useMemo(() => {
    if (!basePillarData) return null;
    if (!ownerAvatarUrl && !ownerInfo) return basePillarData;
    return {
      ...basePillarData,
      ...(ownerAvatarUrl ? { ownerAvatarUrl } : {}),
      // DEV-991: surface kind/guid/displayName so PillarPageHeader can make
      // the avatar a hyperlink to the owner's page.
      ...(ownerInfo && ownerInfo.kind !== 'unknown'
          ? { ownerKind: ownerInfo.kind, ownerGuid: ownerInfo.guid, ownerDisplayName: ownerInfo.displayName }
          : {}),
    };
  }, [basePillarData, ownerAvatarUrl, ownerInfo]);

  /* ---- Resolve hero image from config overrides ---- */
  const resolveHeroUrl = useCallback((): string | null => {
    if (!id) return null;
    try {
      const ov = getProjectOverride(id) as any;
      if (ov?.tile_image_url) return mediaSrc(ov.tile_image_url as string);
      if (ov?.logo_url) return mediaSrc(ov.logo_url as string);
    } catch { /* ignore */ }
    return mediaSrc(pillarData?.heroImageUrl || pillarData?.logoUrl || null);
  }, [id, pillarData]);

  // Direct state for the hero URL — avoids indirection through store reads in useMemo
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(resolveHeroUrl);

  // Sync from external changes (e.g. pillarData updated via API refetch)
  useEffect(() => {
    const resolved = resolveHeroUrl();
    if (resolved) setHeroImageUrl(resolved);
  }, [resolveHeroUrl]);

  /* ---- Hero edit gate ---- */
  const canEditHero = canAdminProject(currentUserGuid, id || '');

  /* ---- Management tab access control ---- */
  const canEditProject = canEditProjectContent(currentUserGuid, id || '');
  const canAdminProj = canAdminProject(currentUserGuid, id || '');

  /* DEV-1050 cutover complete — shadow-mode V1↔V2 parity observer removed
     along with the V1 evaluator. V2 is authoritative. */
  const { slots: roleSlots } = useApiProjectRoles(id);

  /** True when the current user has an active assignment on any role slot in this project. */
  const isTeamMember = useMemo(() => {
    if (canAdminProj) return true; // PM/Owner/Admin always counts
    const ug = currentUserGuid.toUpperCase();
    return roleSlots.some((slot) =>
      (slot.assignments ?? []).some(
        (a) =>
          a.userGuid.toUpperCase() === ug &&
          (a.status === 'active' || a.status === 'pending_verification' || a.status === 'pending_escalated_review')
      )
    );
  }, [canAdminProj, currentUserGuid, roleSlots]);

  /** Management tab: hidden for non-members, read-only for team members, full for PM/Admin. */
  const showManagementTab = canAdminProj || canEditProject || isTeamMember;

  /** Viewer tier for member visibility filtering on team displays. */
  const viewerTier: 'admin' | 'team' | 'public' = canAdminProj
    ? 'admin'
    : isTeamMember
      ? 'team'
      : 'public';

  const [showTeamList, setShowTeamList] = useState(false);

  /* ---- Hero edit: shared hook (Path Y) ---- */
  // useHeroEdit owns crop state, dropdown state, URL input state, the file
  // input, MinIO upload, and the re-crop fetch. It returns the menu node
  // (passed to PillarPageShell heroEditControls), the overlay node (passed
  // to heroCropOverlay), and menuOpen (passed to heroMenuOpen so the shell
  // can let the dropdown overflow the header).
  const persistFullProjectHero = useCallback(
    (url: string) => {
      if (!id) return;
      // Update store for persistence / API write-through.
      setProjectOverride(id, { tile_image_url: url });
      // DIRECTLY set the displayed URL — no store round-trip needed.
      setHeroImageUrl(url);
      onProjectConfigChanged?.();
    },
    [id, onProjectConfigChanged],
  );
  const removeFullProjectHero = useCallback(() => {
    if (!id) return;
    setProjectOverride(id, { tile_image_url: '' });
    setHeroImageUrl(null);
    onProjectConfigChanged?.();
  }, [id, onProjectConfigChanged]);
  const {
    menu: heroEditControls,
    overlay: heroCropOverlay,
    menuOpen: heroMenuOpen,
  } = useHeroEdit({
    canEdit: canEditHero,
    currentHeroUrl: heroImageUrl,
    uploadBucket: 'heroes',
    uploadEntityKey: id,
    onPersist: persistFullProjectHero,
    onRemove: removeFullProjectHero,
  });

  /* ---- Tab state ---- */
  const [activeTabIndex, setActiveTabIndex] = useState(() =>
    tabKeyToIndex(tab, [...ALL_TAB_KEYS])
  );

  // Compute visible tab keys before the early return so hooks are unconditional.
  // When Management tab is hidden, remove 'work' from the key list.
  const visibleTabKeys = useMemo(() => {
    const keys = [...ALL_TAB_KEYS] as string[];
    if (!showManagementTab) {
      const idx = keys.indexOf('work');
      if (idx >= 0) keys.splice(idx, 1);
    }
    return keys;
  }, [showManagementTab]);

  // Re-sync active tab index when URL tab key or visible tabs change
  useEffect(() => {
    const resolved = tabKeyToIndex(tab, visibleTabKeys);
    setActiveTabIndex(resolved);
  }, [tab, visibleTabKeys]);

  const handleTabChange = useCallback(
    (index: number) => {
      setActiveTabIndex(index);
      const key = visibleTabKeys[index] ?? 'about';
      navigate(`/projects/${id}/${key}`, { replace: true });
    },
    [id, navigate, visibleTabKeys]
  );

  /* Work sub-tab state — lifted so sub-tab bar can render outside content area.
   * Initialise from location state when arriving via "View Full" so the user
   * lands on the same sub-tab they were viewing in the compact panel. */
  const [workSubTab, setWorkSubTab] = useState<WorkSubTabKey>(incomingWorkSubTab ?? 'performance');
  const [ganttOpen, setGanttOpen] = useState(false);
  const preGanttTabRef = useRef<WorkSubTabKey>('performance');
  const handleOpenGantt = useCallback(() => {
    preGanttTabRef.current = workSubTab;
    setGanttOpen(true);
  }, [workSubTab]);
  const handleCloseGantt = useCallback(() => {
    setGanttOpen(false);
    setWorkSubTab(preGanttTabRef.current);
  }, []);

  /* ---- Edit project modal ---- */
  const [isEditOpen, setIsEditOpen] = useState(false);
  const handleEdit = useCallback(() => setIsEditOpen(true), []);
  const handleEditClose = useCallback(() => setIsEditOpen(false), []);
  const handleEditSaved = useCallback(() => {
    setIsEditOpen(false);
    setHeroImageUrl(resolveHeroUrl()); // re-resolve hero image from store
    onProjectConfigChanged?.();
  }, [onProjectConfigChanged, resolveHeroUrl]);

  /* ---- DEV-841 / DEV-840 Path A ----
   * The previous DEV-841 wiring used a window event listener here that
   * opened ProjectConfigModal scoped to a single KPI. That was visually
   * noisy (modal background flashes before the wizard tray slides in) and
   * coupled wizard save to the modal's atomic Save click.
   *
   * Replaced by <KpiWizardLauncher /> below — it owns the listener, the
   * tray, and a direct-save handler that hits the same persist API the
   * modal uses. No modal hop, single Save click, identical persistence.
   */

  /* ---- Navigation callbacks (must be before early return) ---- */
  // Close priority: explicit returnTo → browser back (if there's history) → /my-projects
  const handleClose = useCallback(() => {
    if (returnTo) { navigate(returnTo); return; }
    // window.history.length > 2 means there's a real in-app page to go back to
    // (length is 1 for a fresh tab, 2 after the first navigation)
    if (window.history.length > 2) { navigate(-1); return; }
    navigate('/my-projects');
  }, [navigate, returnTo]);
  /** Restore = return to split-screen with this project still selected.
   * Passes the current workSubTab so the compact panel restores to the
   * same sub-section the user was viewing in full view. */
  const handleRestore = useCallback(
    () => navigate(returnTo || '/portfolio', {
      state: {
        restoreProjectGuid: restoreProjectGuid || id,
        workSubTab,
      },
    }),
    [navigate, returnTo, restoreProjectGuid, id, workSubTab],
  );

  /* ---- Not found ---- */
  if (!pillarData || !resolvedRawProject) {
    return (
      <div className={styles.page} style={{ alignItems: 'center', justifyContent: 'center' }}>
        <h2 style={{ color: 'var(--text-tertiary)' }}>Project Not Found</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
          No project matches ID: <code>{id}</code>
        </p>
        <button
          onClick={() => navigate('/')}
          style={{
            marginTop: 12,
            padding: '8px 20px',
            borderRadius: 8,
            border: 'none',
            background: 'var(--accent-secondary)',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Back to My Projects
        </button>
      </div>
    );
  }

  /* ---- Tab definitions ---- */
  /* Management tab is gated by access level:
   *   - Hidden for public / non-members
   *   - Read-only for team members (canEdit=false passed to WorkTabPanel)
   *   - Full for PM / Owner / System Admin                             */
  /** PM/Owner/SystemAdmin can edit; team members get read-only Management tab. */
  const canEditManagement = canAdminProj || canEditProject;

  const allTabs: TabDefinition[] = [
    ...(showManagementTab
      ? [
          {
            key: 'work',
            label: 'Management',

            content: (
              <WorkTabPanel
                data={pillarData}
                projectPayload={resolvedEnrichedRow}
                currentUserGuid={currentUserGuid}
                canEdit={canEditManagement}
                debugMode={debugMode}
                onProjectConfigChanged={onProjectConfigChanged}
                activeSubTab={workSubTab}
                onSubTabChange={setWorkSubTab}
                ganttOpen={ganttOpen}
                onOpenGantt={handleOpenGantt}
                onCloseGantt={handleCloseGantt}
                initialTeamView={incomingTeamView}
              />
            ),
          },
        ]
      : []),
    {
      key: 'impact',
      label: 'Impact',

      content: <ImpactTabPanel data={pillarData} projectGuid={pillarData.guid} currentUserGuid={currentUserGuid} projectPayload={resolvedEnrichedRow} />,
    },
    {
      key: 'about',
      label: 'About',

      content: <CoreTabPanel data={pillarData} projectGuid={pillarData.guid} viewerTier={viewerTier} onShowTeamList={() => setShowTeamList(true)} />,
    },
    {
      key: 'expert',
      label: 'Journal',

      content: <JournalTab entityType="project" entityGuid={pillarData.guid} canShare={canEditProject} />,
    },
    {
      key: 'community',
      label: 'Community',

      content: <CommunityTabPanel data={pillarData} />,
    },
    {
      key: 'media',
      label: 'Media',

      content: <MediaTabPanel projectGuid={pillarData.guid} currentUserGuid={currentUserGuid} debugMode={debugMode} />,
    },
  ];

  // heroEditControls, heroCropOverlay, and heroMenuOpen are provided by
  // the useHeroEdit hook (see above). They flow straight into PillarPageShell.

  if (showTeamList) {
    return (
      <div className={styles.page}>
        <TeamListPage
          projectGuid={pillarData.guid}
          projectName={pillarData.name}
          viewerTier={viewerTier}
          onBack={() => setShowTeamList(false)}
        />
      </div>
    );
  }

  return (
    <div className={styles.page} style={{ position: 'relative' }}>
      {/* DEV-1452: in-app back button removed (LeftNav + browser back cover it).
          The "return to split view" restore affordance below is NOT a back
          button — it restores the portfolio's selection, which history cannot. */}
      <PillarPageShell
        pillarType="projects"
        data={pillarData}
        tabs={allTabs}
        activeTabIndex={activeTabIndex}
        onTabChange={handleTabChange}
        heroImageUrl={heroImageUrl}
        heroEditControls={heroEditControls}
        headerActions={<FollowButton targetType="project" targetGuid={pillarData.guid} compact showCount={false} />}
        heroCropOverlay={heroCropOverlay}
        isHeroExpanded
        heroMenuOpen={heroMenuOpen}
        onEdit={handleEdit}
        onRestore={returnTo ? handleRestore : undefined}
        onClose={handleClose}
      />

      {/* Edit project modal */}
      <ProjectConfigModal
        isOpen={isEditOpen}
        onClose={handleEditClose}
        project={resolvedRawProject}
        onSaved={handleEditSaved}
      />

      {/* DEV-841 / DEV-840 Path A: see KpiWizardLauncher for rationale. */}
      <KpiWizardLauncher
        projectGuid={String(resolvedRawProject?.project_GUID ?? resolvedRawProject?.project_guid ?? '')}
        onSaved={handleEditSaved}
      />
    </div>
  );
}

